/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${search}`;
  }
  if (label) {
    params = `${params}&labels[]=${label}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  // Aceleres -- as identidades que o contato JA reune, uma por endereco, com as
  // conexoes agrupadas dentro de cada uma. Nao confundir com
  // `fetchContactableInbox`, que responde por onde da para INICIAR uma conversa
  // e, para isso, colapsa os contact_inboxes e inventa source_id.
  // Ver Aceleres::ContactIdentities no backend.
  identities(id, { conversationId } = {}) {
    return axios.get(`${this.url}/${id}/identities`, {
      params: conversationId ? { conversation_id: conversationId } : {},
    });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  // Aceleres -- o que a remocao preserva e o que destroi, e o codigo de
  // confirmacao quando ha conversa em aberto. Ver
  // Aceleres::ContactRemovalConfirmation no backend.
  removalPreview(id) {
    return axios.get(`${this.url}/${id}/removal_preview`);
  }

  // O `delete` herdado do ApiClient nao leva parametros; a confirmacao viaja
  // na query porque DELETE com corpo nao e tratado de forma uniforme.
  deleteWithConfirmation(id, confirmationCode) {
    const query = confirmationCode
      ? `?confirmation_code=${encodeURIComponent(confirmationCode)}`
      : '';
    return axios.delete(`${this.url}/${id}${query}`);
  }

  getAceleresClient(id) {
    return axios.get(`${this.url}/${id}/aceleres_client`);
  }

  updateAceleresClient(id, data) {
    return axios.patch(`${this.url}/${id}/aceleres_client`, data);
  }

  instagramMergeSuggestions(id, provider) {
    return axios.get(`${this.url}/${id}/instagram_merge_suggestions`, {
      params: provider ? { provider } : undefined,
    });
  }

  confirmInstagramMerge(id, suggestionId, provider) {
    return axios.post(`${this.url}/${id}/instagram_merge_suggestions`, {
      suggestion_id: suggestionId,
      ...(provider ? { provider } : {}),
    });
  }

  updateTeamAssignment(id, teamId) {
    return axios.patch(`${this.url}/${id}/team_assignment`, {
      team_id: teamId,
    });
  }

  getConversations(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations`);
  }

  // Lista do modal de encaminhamento: já vem paginada de verdade, consolidada
  // por telefone e ordenada pela conversa mais recente. A listagem geral de
  // contatos não serve aqui — o modal tinha de baixá-la inteira, 15 por vez.
  getForwardDirectory(page = 1, search = '') {
    const params = new URLSearchParams({ page });
    if (search) params.append('q', search);
    return axios.get(`${this.url}/forward_directory?${params.toString()}`);
  }

  // Destinos possíveis ao encaminhar para este contato: conversas que já
  // existem e conexões onde daria para abrir uma nova, cada uma já com o
  // motivo do impedimento quando houver.
  getForwardTargets(contactId, sourceConversationId) {
    const params = sourceConversationId
      ? `?source_conversation_id=${sourceConversationId}`
      : '';
    return axios.get(`${this.url}/${contactId}/forward_targets${params}`);
  }

  getContactableInboxes(contactId) {
    if (!contactId) {
      return Promise.resolve({ data: { payload: [] } });
    }

    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  sendContactCard(contactId, payload) {
    return axios.post(`${this.url}/${contactId}/send_contact_card`, payload);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '', options = {}) {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL, { signal: options.signal });
  }

  active(page = 1, sortAttr = 'name') {
    let requestURL = `${this.url}/active?${buildContactParams(page, sortAttr)}`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }
}

export default new ContactAPI();
